import classNames from 'classnames/bind';
import { To } from 'history';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { DropdownItemProps } from 'semantic-ui-react';
import { Dropdown } from '@creditsights/styleguide-web';
import styles from './ResultsFilter.module.scss';

interface ResultsFilterProps {
  values: number[];
  onResultsCountChange: (value: number) => void;
  onShowAllSelected?: () => void;
  showAllLoadItemCount?: number;
  selectedValue: number;
  allValueLink?: To;
  optionTestIdPrefix?: string;
}

const cx = classNames.bind(styles);

const ALL_VALUE = 'all';
const ALL_VALUE_TEXT = 'All';

const getOptions = (
  values: number[],
  optionTestIdPrefix: string,
  allValueLink?: To,
  onShowAllSelected?: () => void
) => {
  const options: DropdownItemProps[] = values.map((value) => ({
    text: value,
    value,
    key: value,
    'data-testid': `${optionTestIdPrefix}-${value}`,
  }));
  if (allValueLink || onShowAllSelected) {
    options.push({
      text: ALL_VALUE_TEXT,
      value: ALL_VALUE,
      key: ALL_VALUE,
      'data-testid': `${optionTestIdPrefix}-${ALL_VALUE}`,
    });
  }
  return options;
};

const ResultsFilter = ({
  selectedValue,
  values,
  onResultsCountChange,
  onShowAllSelected,
  showAllLoadItemCount,
  optionTestIdPrefix = 'results-filter-option',
  allValueLink,
}: ResultsFilterProps): React.ReactElement => {
  const options: DropdownItemProps[] = getOptions(values, optionTestIdPrefix, allValueLink, onShowAllSelected);
  const navigate = useNavigate();

  const onChange = (value) => {
    if (value === ALL_VALUE) {
      onShowAllSelected?.();
      if (allValueLink) {
        navigate(allValueLink);
      }
    } else if (value && typeof value === 'number') {
      onResultsCountChange(value);
    }
  };

  const resolvedSelectedValue = selectedValue === showAllLoadItemCount ? ALL_VALUE : selectedValue;
  const selectedValueText = resolvedSelectedValue === ALL_VALUE ? ALL_VALUE_TEXT : resolvedSelectedValue;
  return (
    <div className={cx('body')}>
      <Dropdown
        className={cx('dropdown')}
        value={resolvedSelectedValue}
        options={options}
        onOptionSelect={onChange}
        text={`Show ${selectedValueText}`}
        dataTestId="results-filter"
        withCaretIcon
        withCheckmarks
      />
    </div>
  );
};

export default ResultsFilter;
